import { Fragment, ReactNode } from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

export interface ToastProps {
  id: string;
  variant?: ToastVariant;
  title: string;
  message?: string;
  duration?: number;
  isVisible: boolean;
  onClose: (id: string) => void;
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'card border-l-4 border-accent-500',
  error: 'card border-l-4 border-red-500',
  warning: 'card border-l-4 border-amber-500',
  info: 'card border-l-4 border-primary-500',
};

const iconStyles: Record<ToastVariant, string> = {
  success: 'text-accent-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-primary-500',
};

const icons: Record<ToastVariant, ReactNode> = {
  success: <CheckCircleIcon className="h-6 w-6" />,
  error: <XCircleIcon className="h-6 w-6" />,
  warning: <ExclamationTriangleIcon className="h-6 w-6" />,
  info: <InformationCircleIcon className="h-6 w-6" />,
};

export const Toast = ({
  id,
  variant = 'info',
  title,
  message,
  isVisible,
  onClose,
}: ToastProps) => {
  return (
    <Transition
      show={isVisible}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className={clsx(
          'max-w-sm w-full shadow-card rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5',
          variantStyles[variant]
        )}
      >
        <div className="p-4">
          <div className="flex items-start">
            <div className={clsx('flex-shrink-0', iconStyles[variant])}>
              {icons[variant]}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{title}</p>
              {message && (
                <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{message}</p>
              )}
            </div>
            <div className="ml-4 flex-shrink-0 flex">
              <button
                onClick={() => onClose(id)}
                className="inline-flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

// Toast container component
export interface ToastContainerProps {
  position?: ToastPosition;
  children: ReactNode;
}

const positionStyles: Record<ToastPosition, string> = {
  'top-right': 'top-0 right-0',
  'top-left': 'top-0 left-0',
  'bottom-right': 'bottom-0 right-0',
  'bottom-left': 'bottom-0 left-0',
  'top-center': 'top-0 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
};

export const ToastContainer = ({
  position = 'top-right',
  children,
}: ToastContainerProps) => {
  return (
    <div
      aria-live="assertive"
      className={clsx(
        'fixed z-50 inset-0 flex flex-col items-end px-4 py-6 pointer-events-none sm:p-6',
        positionStyles[position]
      )}
    >
      <div className="flex flex-col gap-4 w-full sm:items-end">
        {children}
      </div>
    </div>
  );
};
